// ══════════════════════════════════════════════════════════════════════════════
// RECHERCHE ARTISANS - Connecté à l'API réelle
// Permet aux particuliers de trouver facilement les bons artisans
// ══════════════════════════════════════════════════════════════════════════════

import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Search, MapPin, Star, Phone, MessageCircle, Bookmark, BookmarkCheck, 
  Filter, ChevronDown, ChevronRight, Loader2, X, Award, Clock, Shield,
  Zap, Droplets, Hammer, Paintbrush, Wrench, Flame, Building2, TreeDeciduous,
  Scissors, Dumbbell, ShoppingBag, Sparkles, BadgeCheck, Euro, RefreshCw,
  Heart, ExternalLink, Eye
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// ══════════════════════════════════════════════════════════════════════════════
// MÉTIERS AVEC ICÔNES
// ══════════════════════════════════════════════════════════════════════════════

const METIERS = [
  { id: 'all', label: 'Tous les métiers', icon: Sparkles },
  { id: 'electricien', label: 'Électricien', icon: Zap, color: '#F59E0B' },
  { id: 'plombier', label: 'Plombier', icon: Droplets, color: '#3B82F6' },
  { id: 'macon', label: 'Maçon', icon: Building2, color: '#EA580C' },
  { id: 'carreleur', label: 'Carreleur', icon: Building2, color: '#10B981' },
  { id: 'peintre', label: 'Peintre', icon: Paintbrush, color: '#EC4899' },
  { id: 'menuisier', label: 'Menuisier', icon: Hammer, color: '#D97706' },
  { id: 'couvreur', label: 'Couvreur', icon: Building2, color: '#8B5CF6' },
  { id: 'chauffagiste', label: 'Chauffagiste', icon: Flame, color: '#EF4444' },
  { id: 'jardinier', label: 'Jardinier', icon: TreeDeciduous, color: '#22C55E' },
  { id: 'serrurier', label: 'Serrurier', icon: Wrench, color: '#64748B' },
  { id: 'coiffeur', label: 'Coiffeur', icon: Scissors, color: '#F472B6' },
  { id: 'coach', label: 'Coach sportif', icon: Dumbbell, color: '#6366F1' },
];

// ══════════════════════════════════════════════════════════════════════════════
// BADGES
// ══════════════════════════════════════════════════════════════════════════════

const BADGES_MAP = {
  'rge': { label: 'RGE', icon: Shield, color: '#22C55E' },
  'qualibat': { label: 'Qualibat', icon: Award, color: '#3B82F6' },
  'reponse-rapide': { label: 'Réponse rapide', icon: Clock, color: '#F59E0B' },
  'devis-gratuit': { label: 'Devis gratuit', icon: Euro, color: '#10B981' },
  'urgences': { label: 'Urgences 24h', icon: Zap, color: '#EF4444' },
  'assurance': { label: 'Assurance décennale', icon: Shield, color: '#8B5CF6' },
  'verified': { label: 'Vérifié', icon: BadgeCheck, color: '#3B82F6' },
};

// ══════════════════════════════════════════════════════════════════════════════
// CARTE ARTISAN
// ══════════════════════════════════════════════════════════════════════════════

function ArtisanCard({ artisan, onToggleFavori, onContact, onViewProfile, isFavori }) {
  // Utiliser metier_name ou metier
  const metierName = artisan.metier_name || artisan.metier || 'Artisan';
  
  const metierData = METIERS.find(m => 
    metierName?.toLowerCase().includes(m.id) || 
    m.label.toLowerCase().includes(metierName?.toLowerCase())
  ) || METIERS[0];
  
  const MetierIcon = metierData.icon;
  
  // Générer des badges à partir des données
  const badges = [];
  if (artisan.is_verified) badges.push('verified');
  if (artisan.response_time && artisan.response_time < 2) badges.push('reponse-rapide');
  if (artisan.free_quote !== false) badges.push('devis-gratuit');
  if (artisan.certifications?.includes('RGE')) badges.push('rge');
  if (artisan.certifications?.includes('Qualibat')) badges.push('qualibat');
  if (artisan.has_insurance) badges.push('assurance');

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition-all group"
      data-testid={`artisan-card-${artisan.seller_id}`}
    >
      <div className="p-5">
        <div className="flex items-start gap-4">
          {/* Avatar / Photo */}
          <div className="relative flex-shrink-0">
            {artisan.profile_photo ? (
              <img 
                src={artisan.profile_photo} 
                alt={artisan.company_name}
                className="w-16 h-16 rounded-xl object-cover"
              />
            ) : (
              <div 
                className="w-16 h-16 rounded-xl flex items-center justify-center text-white text-xl font-bold"
                style={{ backgroundColor: metierData.color || '#1E3A5F' }}
              >
                {artisan.company_name?.charAt(0) || artisan.artisan_name?.charAt(0) || 'A'}
              </div>
            )}
            {artisan.is_verified && (
              <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center border-2 border-white">
                <BadgeCheck size={14} className="text-white" />
              </div>
            )}
          </div>

          {/* Infos principales */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div>
                <h3 className="font-bold text-gray-900 group-hover:text-[#1E3A5F] transition-colors">
                  {artisan.company_name || artisan.artisan_name}
                </h3>
                <p className="text-sm font-medium flex items-center gap-1.5" style={{ color: metierData.color }}>
                  <MetierIcon size={14} />
                  {metierName}
                </p>
              </div>
              <button
                onClick={(e) => { e.stopPropagation(); onToggleFavori(artisan.seller_id); }}
                className={`p-2 rounded-lg transition-all ${
                  isFavori 
                    ? 'bg-red-50 text-red-500' 
                    : 'hover:bg-gray-100 text-gray-400'
                }`}
                data-testid={`favori-btn-${artisan.seller_id}`}
              >
                <Heart size={20} className={isFavori ? 'fill-current' : ''} />
              </button>
            </div>
            
            {/* Note et localisation */}
            <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
              {artisan.rating > 0 && (
                <span className="flex items-center gap-1">
                  <Star size={14} className="text-amber-400 fill-amber-400" />
                  <span className="font-semibold text-gray-900">{artisan.rating?.toFixed(1)}</span>
                  <span className="text-gray-400">({artisan.reviews_count || 0} avis)</span>
                </span>
              )}
              {artisan.city && (
                <span className="flex items-center gap-1">
                  <MapPin size={14} />
                  {artisan.city}
                  {artisan.distance && <span className="text-gray-400">({artisan.distance})</span>}
                </span>
              )}
            </div>

            {/* Description */}
            {artisan.description && (
              <p className="mt-2 text-sm text-gray-600 line-clamp-2">{artisan.description}</p>
            )}

            {/* Badges */}
            {badges.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-3">
                {badges.slice(0, 4).map(badgeId => {
                  const badge = BADGES_MAP[badgeId];
                  if (!badge) return null;
                  const BadgeIcon = badge.icon;
                  return (
                    <span 
                      key={badgeId}
                      className="px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1"
                      style={{ backgroundColor: `${badge.color}15`, color: badge.color }}
                    >
                      <BadgeIcon size={12} />
                      {badge.label}
                    </span>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Prestations (si disponibles) */}
        {artisan.prestations && artisan.prestations.length > 0 && (
          <div className="mt-4 pt-4 border-t border-gray-100">
            <p className="text-xs font-medium text-gray-500 mb-2">Prestations populaires</p>
            <div className="flex flex-wrap gap-2">
              {artisan.prestations.slice(0, 3).map((prestation, idx) => (
                <span 
                  key={idx}
                  className="px-3 py-1.5 bg-gray-50 rounded-lg text-sm text-gray-700 flex items-center gap-2"
                >
                  <span>{prestation.name || prestation.title}</span>
                  {(prestation.price_from || prestation.price) && (
                    <span className="text-[#F97316] font-semibold">
                      {prestation.price_type === 'from' || prestation.price_from ? 'dès ' : ''}
                      {prestation.price_from || prestation.price}€
                    </span>
                  )}
                </span>
              ))}
              {artisan.prestations_count > 3 && (
                <span className="px-3 py-1.5 bg-blue-50 text-blue-600 rounded-lg text-sm font-medium">
                  +{artisan.prestations_count - 3} autres
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center border-t border-gray-100">
        <button
          onClick={() => onViewProfile(artisan)}
          className="flex-1 py-3 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
          data-testid={`view-profile-${artisan.seller_id}`}
        >
          <Eye size={16} />
          Voir le profil
        </button>
        <div className="w-px h-8 bg-gray-100" />
        <button
          onClick={() => onContact(artisan, 'phone')}
          className="flex-1 py-3 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
        >
          <Phone size={16} />
          Appeler
        </button>
        <div className="w-px h-8 bg-gray-100" />
        <button
          onClick={() => onContact(artisan, 'devis')}
          className="flex-1 py-3 text-sm font-medium text-[#F97316] hover:bg-orange-50 transition-colors flex items-center justify-center gap-2"
          data-testid={`demander-devis-${artisan.seller_id}`}
        >
          <MessageCircle size={16} />
          Demander un devis
        </button>
      </div>
    </motion.div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// PAGE PRINCIPALE
// ══════════════════════════════════════════════════════════════════════════════

export default function RechercheArtisans() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // États
  const [artisans, setArtisans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalResults, setTotalResults] = useState(0);
  const [favoris, setFavoris] = useState(new Set());
  
  // Filtres
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [selectedMetier, setSelectedMetier] = useState(searchParams.get('metier') || 'all');
  const [selectedCity, setSelectedCity] = useState(searchParams.get('city') || '');
  const [minRating, setMinRating] = useState(searchParams.get('rating') || '');
  const [showFilters, setShowFilters] = useState(false);
  const [sortBy, setSortBy] = useState('rating');
  
  // Refs et états pour le scroll des filtres mobile
  const scrollContainerRef = useRef(null);
  const [showScrollHint, setShowScrollHint] = useState(true);
  
  // Gérer le scroll des filtres
  const handleScrollFilters = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setShowScrollHint(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  // Charger les artisans depuis l'API
  const fetchArtisans = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.set('search', searchQuery);
      if (selectedMetier !== 'all') params.set('metier_slug', selectedMetier);
      if (selectedCity) params.set('city', selectedCity);
      if (minRating) params.set('min_rating', minRating);
      if (sortBy === 'rating') params.set('sort_by', 'note');
      else if (sortBy === 'reviews') params.set('sort_by', 'avis');
      else if (sortBy === 'newest') params.set('sort_by', 'recent');
      params.set('per_page', '50');

      const response = await fetch(`${API_URL}/api/artisans?${params.toString()}`);
      const data = await response.json();

      if (data.items || data.artisans) {
        const artisansList = data.items || data.artisans || [];
        setArtisans(artisansList);
        setTotalResults(data.total || artisansList.length);
      } else if (Array.isArray(data)) {
        setArtisans(data);
        setTotalResults(data.length);
      } else {
        setArtisans([]);
        setTotalResults(0);
      }
    } catch (err) {
      console.error('Erreur chargement artisans:', err);
      toast.error('Erreur lors du chargement des artisans');
      setArtisans([]);
    } finally {
      setLoading(false);
    }
  }, [searchQuery, selectedMetier, selectedCity, minRating, sortBy]);

  // Charger les favoris de l'utilisateur
  const fetchFavoris = async () => {
    try {
      const token = localStorage.getItem('social_token') || localStorage.getItem('buyer_token');
      if (!token) return;

      const response = await fetch(`${API_URL}/api/ecosystem/profile/me/followed-artisans`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      
      if (data.followed_artisans) {
        const favSet = new Set(data.followed_artisans.map(f => f.artisan?.seller_id));
        setFavoris(favSet);
      }
    } catch (err) {
      console.error('Erreur chargement favoris:', err);
    }
  };

  useEffect(() => {
    fetchArtisans();
    fetchFavoris();
  }, [fetchArtisans]);

  // Mettre à jour les URL params
  useEffect(() => {
    const params = new URLSearchParams();
    if (searchQuery) params.set('q', searchQuery);
    if (selectedMetier !== 'all') params.set('metier', selectedMetier);
    if (selectedCity) params.set('city', selectedCity);
    if (minRating) params.set('rating', minRating);
    setSearchParams(params, { replace: true });
  }, [searchQuery, selectedMetier, selectedCity, minRating, setSearchParams]);

  // Toggler favori
  const handleToggleFavori = async (artisanId) => {
    const token = localStorage.getItem('social_token') || localStorage.getItem('buyer_token');
    if (!token) {
      toast.error('Connecte-toi pour ajouter aux favoris');
      return;
    }

    try {
      const isFav = favoris.has(artisanId);
      const endpoint = isFav ? 'unfollow' : 'follow';
      
      await fetch(`${API_URL}/api/ecosystem/artisans/${artisanId}/${endpoint}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      setFavoris(prev => {
        const newSet = new Set(prev);
        if (isFav) {
          newSet.delete(artisanId);
          toast.success('Retiré des favoris');
        } else {
          newSet.add(artisanId);
          toast.success('Ajouté aux favoris');
        }
        return newSet;
      });
    } catch (err) {
      console.error('Erreur toggle favori:', err);
    }
  };

  // Contact
  const handleContact = (artisan, type) => {
    if (type === 'phone' && artisan.phone) {
      window.location.href = `tel:${artisan.phone}`;
    } else if (type === 'devis') {
      // Naviguer vers le formulaire de demande de devis
      navigate(`/cockpit-particulier/artisans/devis?artisan=${artisan.seller_id}&name=${encodeURIComponent(artisan.company_name || artisan.artisan_name)}`);
    }
  };

  // Voir le profil
  const handleViewProfile = (artisan) => {
    navigate(`/artisans/${artisan.seller_id}`);
  };

  // Reset filtres
  const handleResetFilters = () => {
    setSearchQuery('');
    setSelectedMetier('all');
    setSelectedCity('');
    setMinRating('');
  };

  const hasActiveFilters = searchQuery || selectedMetier !== 'all' || selectedCity || minRating;

  return (
    <div className="max-w-5xl mx-auto space-y-6 pb-20">
      {/* Header */}
      <div>
        <h1 className="text-[16px] sm:text-xl font-bold text-[#1e3a5f]">Trouver un artisan</h1>
        <p className="text-gray-500 mt-1">Des professionnels qualifiés et vérifiés près de chez toi</p>
      </div>

      {/* Barre de recherche */}
      <div className="flex gap-3">
        <div className="flex items-center gap-2 px-3 py-2 flex-1 border border-gray-200 rounded-xl">
          <Search size={14} className="text-gray-400 flex-shrink-0" />
          <input
            type="text"
            placeholder="Rechercher par nom, spécialité, ville..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="flex-1 bg-transparent border-0 outline-none text-sm text-gray-700 placeholder-gray-400"
            data-testid="search-artisans-input"
          />
        </div>
        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`flex items-center gap-2 px-5 py-3.5 border rounded-xl transition-all ${
            showFilters || hasActiveFilters
              ? 'border-[#1E3A5F] bg-[#1E3A5F] text-white'
              : 'border-gray-200 hover:bg-gray-50'
          }`}
          data-testid="toggle-filters-btn"
        >
          <Filter size={18} />
          Filtres
          {hasActiveFilters && (
            <span className="w-5 h-5 bg-[#F97316] rounded-full text-xs flex items-center justify-center">
              {[searchQuery, selectedMetier !== 'all', selectedCity, minRating].filter(Boolean).length}
            </span>
          )}
        </button>
      </div>

      {/* Filtres avancés */}
      <AnimatePresence>
        {showFilters && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="bg-white rounded-xl border border-gray-200 p-4 space-y-4"
          >
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Ville</label>
                <input
                  type="text"
                  placeholder="Ex: Lyon, Paris..."
                  value={selectedCity}
                  onChange={(e) => setSelectedCity(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#1E3A5F]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Note minimum</label>
                <select
                  value={minRating}
                  onChange={(e) => setMinRating(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#1E3A5F]"
                >
                  <option value="">Toutes les notes</option>
                  <option value="4.5">4.5+ étoiles</option>
                  <option value="4">4+ étoiles</option>
                  <option value="3.5">3.5+ étoiles</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Trier par</label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#1E3A5F]"
                >
                  <option value="rating">Meilleure note</option>
                  <option value="reviews">Plus d'avis</option>
                  <option value="newest">Plus récent</option>
                </select>
              </div>
            </div>
            {hasActiveFilters && (
              <button
                onClick={handleResetFilters}
                className="text-sm text-[#F97316] hover:underline flex items-center gap-1"
              >
                <X size={14} /> Réinitialiser les filtres
              </button>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Pills métiers - Style premium */}
      <div className="bg-white rounded-xl border border-gray-200 p-3">
        {/* Filtres Mobile */}
        <div className="sm:hidden">
          <div className="relative flex items-center">
            <div 
              ref={scrollContainerRef}
              onScroll={handleScrollFilters}
              className="flex gap-1.5 overflow-x-auto scrollbar-hide flex-1 pr-6"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {METIERS.map(metier => {
                const Icon = metier.icon;
                const isActive = selectedMetier === metier.id;
                return (
                  <button
                    key={metier.id}
                    onClick={() => setSelectedMetier(metier.id)}
                    data-testid={`metier-filter-${metier.id}`}
                    className={`flex-shrink-0 px-3 py-2 rounded-xl text-xs font-semibold transition-all whitespace-nowrap flex items-center gap-1.5 ${
                      isActive
                        ? 'bg-orange-500 text-white shadow-sm'
                        : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                    }`}
                  >
                    <Icon size={12} />
                    {metier.label}
                  </button>
                );
              })}
            </div>
            
            {/* Flèche orange discrète */}
            {showScrollHint && (
              <div className="flex-shrink-0 flex items-center pointer-events-none">
                <ChevronRight size={18} className="text-orange-500" strokeWidth={2.5} />
              </div>
            )}
          </div>
        </div>

        {/* Filtres Desktop */}
        <div className="hidden sm:flex flex-wrap gap-2">
          {METIERS.map(metier => {
            const Icon = metier.icon;
            const isActive = selectedMetier === metier.id;
            return (
              <button
                key={metier.id}
                onClick={() => setSelectedMetier(metier.id)}
                data-testid={`metier-filter-${metier.id}`}
                className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors flex items-center gap-2 ${
                  isActive
                    ? 'bg-orange-500 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <Icon size={14} />
                {metier.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Résultats */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-500">
            {loading ? (
              'Recherche en cours...'
            ) : (
              <>{totalResults} artisan{totalResults !== 1 ? 's' : ''} trouvé{totalResults !== 1 ? 's' : ''}</>
            )}
          </p>
          {!loading && (
            <button
              onClick={fetchArtisans}
              className="text-sm text-[#1E3A5F] hover:underline flex items-center gap-1"
            >
              <RefreshCw size={14} /> Actualiser
            </button>
          )}
        </div>
        
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="animate-spin text-[#1E3A5F]" size={32} />
          </div>
        ) : artisans.length === 0 ? (
          <div className="text-center py-20 bg-gray-50 rounded-xl">
            <Search size={48} className="mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-semibold text-gray-700 mb-2">Aucun artisan trouvé</h3>
            <p className="text-gray-500 mb-4">Essaie d'élargir ta recherche ou de modifier les filtres</p>
            {hasActiveFilters && (
              <Button variant="outline" onClick={handleResetFilters}>
                Réinitialiser les filtres
              </Button>
            )}
          </div>
        ) : (
          <div className="grid gap-4">
            {artisans.map(artisan => (
              <ArtisanCard
                key={artisan.seller_id || artisan.id}
                artisan={artisan}
                isFavori={favoris.has(artisan.seller_id)}
                onToggleFavori={handleToggleFavori}
                onContact={handleContact}
                onViewProfile={handleViewProfile}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
